import React, { useState, useEffect } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { 
  DollarSign, Building2, Search, 
  ArrowRight, Check, X,
  FileText, BarChart2, Users,
  XCircle,
  Award, Globe,
  Star, Target, GitBranch
} from 'lucide-react';
import SEOHead from '../components/SEO/SEOHead';
import ContactPromptModalWithAccess from '../components/ContactPromptModalWithAccess';
import apiService from '../services/apiService';
import { 
  logPatentDemo, 
  logListedDemo, 
  logUnlistedDemo, 
  logPatentDemoStart,
  logPatentDemoSuccess,
  logPatentDemoError,
  logCompanyDemoStart,
  logCompanyDemoSuccess,
  logCompanyDemoError,
  logEmailCollection,
  logTabSwitch,
  logCompanySearch,
  logFormValidationError,
  logAccessCodeUsed
} from '../utils/analytics';
import { validateAccessCode, checkValuationLimit, incrementValuationUsage } from '../utils/accessCodes';

// Custom hook for mobile detection
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  return isMobile;
};

// API call functions using the real API
const getPatentValuation = async (patentNumber) => {
  const response = await fetch(`/api/patent/search/${patentNumber}`);
  
  // Read the response text once (can only be read once)
  const text = await response.text();
  
  if (!response.ok) {
    // Try to get error message from response, but handle empty/invalid JSON
    let errorMessage = `Patent search failed: ${response.status} ${response.statusText}`;
    if (text) {
      try {
        const error = JSON.parse(text);
        errorMessage = error.message || errorMessage;
      } catch (e) {
        // If not JSON, use the text as error message (truncate if too long)
        errorMessage = text.length > 200 ? `${text.substring(0, 200)}...` : text;
      }
    }
    throw new Error(errorMessage);
  }
  
  // Check if there's any content before trying to parse JSON
  if (!text) {
    throw new Error('Empty response from server. Please try again.');
  }
  
  let data;
  try {
    data = JSON.parse(text);
  } catch (e) {
    console.error('Invalid JSON response:', text);
    throw new Error('Invalid response from server. Please try again.');
  }
  
  // Check if estimatedValue is a meaningful number
  if (!data.estimatedValue || data.estimatedValue <= 0 || isNaN(data.estimatedValue)) {
    throw new Error('Valuation not available. Please try a different patent number.');
  }
  
  return {
    value: `$${Math.ceil(data.estimatedValue).toLocaleString()}`,
    timestamp: new Date().toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }),
    insights: {
      patentGrade: {
        value: data.grade || 'N/A',
        label: 'Patent Grade',
        description: 'Technical strength'
      },
      medianValue: {
        value: data.medianValue ? `$${Math.ceil(data.medianValue).toLocaleString()}` : 'N/A',
        label: 'Median Value',
        description: 'In this category'
      },
      patentCitations: {
        value: data.citations?.toString() || '0',
        label: 'Patent Citations',
        description: 'Forward citations'
      },
      marketGrowth: {
        value: data.marketGrowth ? `${Number(data.marketGrowth).toFixed(2)}%` : '0.00%',
        label: 'Market Growth',
        description: 'Annual growth rate'
      }
    }
  };
};

const DemoPage = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const isMobile = useIsMobile();
  
  // Access check state - async validation
  const [isAdmin, setIsAdmin] = useState(false);
  const [accessCodeInfo, setAccessCodeInfo] = useState(null);
  const [storedAccessCode, setStoredAccessCode] = useState(null);
  const [isValidatingAccess, setIsValidatingAccess] = useState(true);
  
  // Validate stored access code on mount (async)
  useEffect(() => {
    const validateStoredAccess = async () => {
      setIsValidatingAccess(true);
      let code = null;
      let isGranted = false;
      
      try {
        code = localStorage.getItem('demo_access_code');
        isGranted = localStorage.getItem('demo_access_granted') === 'true';
      } catch (error) {
        console.warn('localStorage access blocked:', error);
        setIsValidatingAccess(false);
        return;
      }
      
      if (isGranted && code) {
        try {
          const validation = await validateAccessCode(code);
          if (validation.valid) {
            setIsAdmin(true);
            setAccessCodeInfo(validation);
            setStoredAccessCode(code);
          } else if (validation.expired) {
            // Clear expired access from localStorage
            try {
              localStorage.removeItem('demo_access_code');
              localStorage.removeItem('demo_access_granted');
            } catch (error) {
              console.warn('Failed to clear localStorage:', error);
            }
            setIsAdmin(false);
            setAccessCodeInfo(null);
            setStoredAccessCode(null);
          } else {
            setIsAdmin(false);
            setAccessCodeInfo(null);
            setStoredAccessCode(null);
          }
        } catch (error) {
          console.error('Error validating access code:', error);
          setIsAdmin(false);
          setAccessCodeInfo(null);
          setStoredAccessCode(null);
        }
      } else {
        setIsAdmin(false);
        setAccessCodeInfo(null);
        setStoredAccessCode(null);
      }
      
      setIsValidatingAccess(false);
    };
    
    validateStoredAccess();
  }, []);
  
  // Modal state
  const [showContactModal, setShowContactModal] = useState(false);
  
  const [activeTab, setActiveTab] = useState('patent'); // 'patent' or 'company'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [showCompanyList, setShowCompanyList] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [patentNumber, setPatentNumber] = useState('');
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [email, setEmail] = useState('');
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [isListedCompany, setIsListedCompany] = useState(false);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  // Add pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [companyValuation, setCompanyValuation] = useState(null);
  const [valuationLimitInfo, setValuationLimitInfo] = useState(null);

  // Show contact modal on page load if not admin (after validation completes)
  useEffect(() => {
    if (!isValidatingAccess && !isAdmin) {
      setShowContactModal(true);
    }
  }, [isAdmin, isValidatingAccess]);

  // Track access code usage when access is granted (after async validation completes)
  useEffect(() => {
    if (isAdmin && storedAccessCode) {
      logAccessCodeUsed(storedAccessCode, 'session');
    }
  }, [isAdmin, storedAccessCode]); // Run when access validation completes and access is granted

  // Update valuation limit info when access code info changes
  useEffect(() => {
    const updateLimitInfo = async () => {
      if (isAdmin && storedAccessCode && accessCodeInfo?.maxValuations) {
        try {
          const limitCheck = await checkValuationLimit(storedAccessCode, accessCodeInfo.maxValuations);
          setValuationLimitInfo({
            ...limitCheck,
            maxValuations: accessCodeInfo.maxValuations
          });
        } catch (error) {
          console.error('Error checking valuation limit:', error);
          setValuationLimitInfo(null);
        }
      } else {
        setValuationLimitInfo(null);
      }
    };
    
    updateLimitInfo();
  }, [isAdmin, storedAccessCode, accessCodeInfo]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredCompanies.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentCompanies = filteredCompanies.slice(startIndex, endIndex);

  // Reset pagination when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filteredCompanies]);

  // Modal handlers
  const handleCloseModal = () => {
    setShowContactModal(false);
  };

  const handlePreviewDemo = () => {
    setShowContactModal(false);
  };

  const resetForm = () => {
    setResult(null);
    setCompanyName('');
    setShowCompanyList(false);
    setSelectedCompany(null);
    setError(null);
    setIsListedCompany(false);
    setLoading(false);
    setIsSearching(false);
    setFilteredCompanies([]);
    setCompanyValuation(null);
    setCurrentPage(1);
  };

  // Clear access code handler - separated functionality
  const handleClearAccessCode = () => {
    try {
      localStorage.removeItem('demo_access_code');
      localStorage.removeItem('demo_access_granted');
    } catch (error) {
      console.warn('Failed to clear localStorage:', error);
    }
    setStoredAccessCode(null);
    setIsAdmin(false);
    setAccessCodeInfo(null);
    setShowContactModal(true);
  };

  // Reset form when component mounts or URL changes
  useEffect(() => {
    resetForm();
    const type = searchParams.get('type');
    if (type === 'patent' || type === 'company') {
      setActiveTab(type);
    }
  }, [searchParams, location.key]);

  useEffect(() => {
    console.log('State Update:', {
      activeTab,
      result,
      loading,
      error
    });
  }, [activeTab, result, loading, error]);


  // Add debounced search function
  const debouncedSearch = async (searchTerm) => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    if (searchTerm.trim().length < 2) {
      setShowCompanyList(false);
      setFilteredCompanies([]);
      return;
    }

    const timeout = setTimeout(async () => {
      try {
        setIsSearching(true);
        const response = await fetch(`/api/company/search/${encodeURIComponent(searchTerm)}`);
        
        if (!response.ok) {
          throw new Error(`Search failed: ${response.status}`);
        }
        
        const results = await response.json();
        console.log('Search results:', results);
        
        if (Array.isArray(results)) {
          // Validate company objects
          const validResults = results.filter(company => 
            company && 
            typeof company === 'object' && 
            company.name &&
            company.type &&
            ['Listed', 'Unlisted'].includes(company.type)
          );
          
          console.log('Valid companies:', validResults);
          setFilteredCompanies(validResults);
          setShowCompanyList(validResults.length > 0);
          
          // Track company search analytics
          logCompanySearch(searchTerm, validResults.length);
        } else {
          setFilteredCompanies([]);
          setShowCompanyList(false);
        }
      } catch (error) {
        console.error('Search error:', error);
        setFilteredCompanies([]);
        setShowCompanyList(false);
      } finally {
        setIsSearching(false);
      }
    }, 500);

    setSearchTimeout(timeout);
  };

  // Add new function to handle search state
  const startNewSearch = () => {
    console.log('startNewSearch called');
    setIsSearching(true);
    setSelectedCompany(null);
    setShowCompanyList(true); // Always show the list when starting a new search
  };

  const handleCompanyNameChange = (e) => {
    const value = e.target.value;
    console.log('handleCompanyNameChange called with:', value);
    setCompanyName(value);
    
    // Only show the list and search if the input differs from the selected company
    if (value !== selectedCompany?.name) {
      setSelectedCompany(null); // Clear selected company if input changes
    if (value.trim().length > 0) {
      startNewSearch();
      debouncedSearch(value);
    } else {
      setShowCompanyList(false);
      setFilteredCompanies([]);
      }
    }
    
    setError(null);
  };

  const handleCompanySelect = async (company) => {
    const startTime = Date.now();
    
    try {
      console.log('Starting company selection for:', company);
      
      // Track company demo start
      logCompanyDemoStart(company.name, company.type);
      
      // Set loading state and clear previous data
      setLoading(true);
      setError(null);
      setResult(null);
      setCompanyValuation(null);
      setShowCompanyList(false);
      setCompanyName(company.name);
      setSelectedCompany(company);
      setIsListedCompany(company.type === 'Listed');
      
      // Validate company type
      if (!company.type || !['Listed', 'Unlisted'].includes(company.type)) {
        throw new Error('Invalid company type');
      }

      if (!company.id) {
        throw new Error('Company ID is missing');
      }

      console.log('Fetching valuation for company:', {
        id: company.id,
        name: company.name,
        type: company.type
      });

      const result = await apiService.getCompanyValuation(company);
      console.log('Received valuation result:', result);

      if (!result) {
        throw new Error('No valuation data received');
      }

      const responseTime = Date.now() - startTime;

      // Format the timestamp
      const formattedDate = result.lastUpdated || result.timestamp || new Date().toISOString();
      const formattedTimestamp = new Date(formattedDate).toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
      });

      // Prepare the complete result object
      const completeResult = {
        ...result,
        timestamp: formattedTimestamp
      };

      // Prepare the valuation object
      const value = company.type === 'Listed' ? result.estimatedPortfolioValue : result.estimatedValue;
      const valuation = {
        value: value || '$0',
        timestamp: formattedTimestamp,
        isOvervalued: result.isOvervalued,
        insights: {
          technologyStrength: result.insights?.technologyStrength || 'Medium',
          patentPortfolio: result.insights?.patentPortfolio || 'Growing',
          marketPosition: result.insights?.marketPosition || 'Stable',
          globalCoverage: result.insights?.globalCoverage || 'Moderate'
        }
      };

      // Update both states in a single render
      setResult(completeResult);
      setCompanyValuation(valuation);
      
      // Increment valuation usage if access code has a limit
      if (storedAccessCode && accessCodeInfo?.maxValuations) {
        try {
          await incrementValuationUsage(storedAccessCode, { company_name: company.name });
          // Update limit info after incrementing
          const limitCheck = await checkValuationLimit(storedAccessCode, accessCodeInfo.maxValuations);
          setValuationLimitInfo({
            ...limitCheck,
            maxValuations: accessCodeInfo.maxValuations
          });
        } catch (error) {
          console.warn('Failed to increment valuation usage:', error);
          // Don't block the user if usage tracking fails
        }
      }
      
      // Track successful company valuation with detailed data
      const valuationAmount = company.type === 'Listed' ? result.estimatedPortfolioValue : result.estimatedValue;
      logCompanyDemoSuccess(company.name, company.type, valuationAmount, responseTime);
      
      // Track company valuation based on type (keep existing events for backwards compatibility)
      if (company.type === 'Listed') {
        logListedDemo(company.name);
      } else {
        logUnlistedDemo(company.name);
      }
      
    } catch (err) {
      console.error('Error in handleCompanySelect:', err);
      const responseTime = Date.now() - startTime;
      
      // Track company demo error
      logCompanyDemoError(company.name, company.type, err.message, responseTime);
      
      setError(err.message || 'Failed to get company valuation');
      setResult(null);
      setCompanyValuation(null);
    } finally {
      setLoading(false);
    }
  };

  const handlePatentNumberChange = (e) => {
    const value = e.target.value;
    setPatentNumber(value);
    setError(null);
  };

  // Add patent number validation function
  const validatePatentNumber = (number) => {
    // Remove any whitespace
    const cleanNumber = number.trim();
    
    // Two valid formats:
    // 1. Just numbers (5-9 digits)
    // 2. 'US' (case insensitive) followed by 5-9 digits
    const numberOnlyRegex = /^\d{5,9}$/;
    const usPatentRegex = /^us\d{5,9}$/i;
    
    if (!numberOnlyRegex.test(cleanNumber) && !usPatentRegex.test(cleanNumber)) {
      throw new Error('Please enter a valid patent number (e.g., 12345678 or US12345678)');
    }
    
    return cleanNumber;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if user has admin access
    if (!isAdmin) {
      setError('Demo access is restricted. Please contact us to request access.');
      return;
    }
    
    // Check valuation limit if access code has one
    if (storedAccessCode && accessCodeInfo?.maxValuations) {
      try {
        const limitCheck = await checkValuationLimit(storedAccessCode, accessCodeInfo.maxValuations);
        if (!limitCheck.allowed) {
          setError(`You have reached the limit of ${accessCodeInfo.maxValuations} valuation(s) for this access code. Please contact us for additional access.`);
          return;
        }
      } catch (error) {
        console.error('Error checking valuation limit:', error);
        // Continue anyway if check fails
      }
    }
    
    setLoading(true);
    setError(null);
    
    const startTime = Date.now();
    
    try {
      if (activeTab === 'patent') {
        // First check if patent number is empty
        if (!patentNumber.trim()) {
          logFormValidationError('patentNumber', 'Please input a valid patent number.', 'patent');
          throw new Error('Please input a valid patent number.');
        }
        
        // Then proceed with existing validation and API call
        const validatedNumber = validatePatentNumber(patentNumber);
        
        // Track patent demo start
        logPatentDemoStart(validatedNumber);
        
        const result = await getPatentValuation(validatedNumber);
        
        if (!result || !result.value) {
          const responseTime = Date.now() - startTime;
          logPatentDemoError(validatedNumber, 'Valuation not available', responseTime);
          throw new Error('Valuation not available. Please try a different patent number.');
        }
        
        const responseTime = Date.now() - startTime;
        
        setResult(result);
        
        // Increment valuation usage if access code has a limit
        if (storedAccessCode && accessCodeInfo?.maxValuations) {
          try {
            await incrementValuationUsage(storedAccessCode, { patent_number: validatedNumber });
            // Update limit info after incrementing
            const limitCheck = await checkValuationLimit(storedAccessCode, accessCodeInfo.maxValuations);
            setValuationLimitInfo({
              ...limitCheck,
              maxValuations: accessCodeInfo.maxValuations
            });
          } catch (error) {
            console.warn('Failed to increment valuation usage:', error);
            // Don't block the user if usage tracking fails
          }
        }
        
        // Track successful patent valuation with detailed data
        logPatentDemoSuccess(validatedNumber, result.value, responseTime);
        logPatentDemo(patentNumber); // Keep existing event for backwards compatibility
        
      } else if (activeTab === 'company') {
        // First check if company name is empty
        if (!companyName.trim()) {
          logFormValidationError('companyName', 'Please type in a company name.', 'company');
          throw new Error('Please type in a company name.');
        }
        
        // Then check if a company is selected
        if (!selectedCompany) {
          logFormValidationError('companySelection', 'Please select a company from the list.', 'company');
          throw new Error('Please select a company from the list.');
        }
        
        // Track company demo start
        logCompanyDemoStart(selectedCompany.name, selectedCompany.type);
        
        const response = await fetch(`/api/company/valuation/${selectedCompany.id}`);
        if (!response.ok) {
          const responseTime = Date.now() - startTime;
          logCompanyDemoError(selectedCompany.name, selectedCompany.type, 'Failed to get company valuation', responseTime);
          throw new Error('Failed to get company valuation');
        }
        
        const data = await response.json();
        const responseTime = Date.now() - startTime;
        
        setCompanyValuation(data);
        
        // Increment valuation usage if access code has a limit
        if (storedAccessCode && accessCodeInfo?.maxValuations) {
          try {
            await incrementValuationUsage(storedAccessCode, { company_name: selectedCompany.name });
            // Update limit info after incrementing
            const limitCheck = await checkValuationLimit(storedAccessCode, accessCodeInfo.maxValuations);
            setValuationLimitInfo({
              ...limitCheck,
              maxValuations: accessCodeInfo.maxValuations
            });
          } catch (error) {
            console.warn('Failed to increment valuation usage:', error);
            // Don't block the user if usage tracking fails
          }
        }
        
        // Determine valuation amount for tracking
        const valuationAmount = selectedCompany.type === 'Listed' ? 
          data.estimatedPortfolioValue : data.estimatedValue;
        
        // Track successful company valuation with detailed data
        logCompanyDemoSuccess(selectedCompany.name, selectedCompany.type, valuationAmount, responseTime);
        
        // Track company valuation based on type (keep existing events for backwards compatibility)
        if (selectedCompany.type === 'Listed') {
          logListedDemo(selectedCompany.name);
        } else {
          logUnlistedDemo(selectedCompany.name);
        }
      }
    } catch (error) {
      const responseTime = Date.now() - startTime;
      
      // Track errors with analytics
      if (activeTab === 'patent' && patentNumber.trim()) {
        logPatentDemoError(patentNumber, error.message, responseTime);
      } else if (activeTab === 'company' && selectedCompany) {
        logCompanyDemoError(selectedCompany.name, selectedCompany.type, error.message, responseTime);
      }
      
      setError(error.message);
      setResult(null); // Clear any previous results
    } finally {
      setLoading(false);
    }
  };

  // Add formatValue function
  const formatValue = (value) => {
    if (!value) return '$0';
    
    // Remove $ and commas, then convert to number
    const numericValue = parseFloat(value.replace(/[$,]/g, ''));
    
    // Since input values are in millions, multiply by 1000000 to get the actual value
    const actualValue = numericValue * 1000000;
    
    // Display in B if >= 1 billion
    if (numericValue >= 1000) {
      return `$${(numericValue / 1000).toFixed(1)}B`;
    }
    // Display in M if >= 1 million
    if (numericValue >= 1) {
      return `$${numericValue.toFixed(1)}M`;
    }
    // Display in K if >= 1 thousand
    if (actualValue >= 1000) {
      return `$${(numericValue * 1000).toFixed(1)}K`;
    }
    // Display plain dollar value if < 1 thousand
    return `$${Math.round(actualValue)}`;
  };

  // Format patent values for mobile display
  const formatPatentValue = (value, isMobile = false) => {
    if (!isMobile) return value; // Return original format on desktop
    
    // Parse the value (remove $ and commas)
    const numericValue = parseFloat(value.replace(/[$,]/g, ''));
    
    // Format to K/M/B on mobile
    if (numericValue >= 1000000000) return `$${(numericValue / 1000000000).toFixed(1)}B`;
    if (numericValue >= 1000000) return `$${(numericValue / 1000000).toFixed(1)}M`;  
    if (numericValue >= 1000) return `$${(numericValue / 1000).toFixed(1)}K`;
    return `$${numericValue.toLocaleString()}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <SEOHead 
        title="Patent Valuation Demo - Try PricePatent Free | IP Analytics Demo"
        description="Try our AI-powered patent valuation demo free. Get instant patent assessments, IP analytics, and licensing insights. No signup required - see real patent valuations now."
        keywords="patent valuation demo, IP analytics demo, free patent valuation, patent assessment trial, IP valuation tool, patent appraisal demo, intellectual property demo"
        ogTitle="Free Patent Valuation Demo - Try PricePatent Now"
        ogDescription="Experience AI-powered patent valuation instantly. Free demo with real patent data - no signup required."
        structuredData={{
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": "PricePatent Demo",
          "description": "Free interactive demo of AI-powered patent valuation platform",
          "url": "https://pricepatent.com/demo",
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "Web Browser",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD",
            "availability": "InStock",
            "description": "Free demo - no signup required"
          },
          "featureList": [
            "Real-time patent valuation",
            "Patent search and analysis", 
            "IP portfolio assessment",
            "Market comparables",
            "Licensing potential analysis"
          ],
          "audience": {
            "@type": "Audience",
            "audienceType": ["IP Professionals", "Investors", "Patent Attorneys", "Technology Companies"]
          }
        }}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center px-4 py-2 bg-blue-50 rounded-full text-blue-600 text-sm font-medium mb-4">
            <DollarSign className="h-4 w-4 mr-2" />
            Try Our Demo
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            <span className="block">IP Valuation</span>
            <span className="block bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent">Real-Time Demo</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Experience our powerful valuation tools with real data. <br></br>Choose between patent or company valuation.
          </p>
        </div>

        {/* Access Code Display - Subtle, above tabs */}
        {storedAccessCode && (
          <div className="flex justify-center -mt-8 mb-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 text-xs text-gray-500 font-mono">
              <span>Access Code: {storedAccessCode}</span>
              <button
                onClick={handleClearAccessCode}
                className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                aria-label="Clear access code"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex rounded-lg bg-gray-100 p-1">
            <button
              onClick={() => {
                const oldTab = activeTab;
                setActiveTab('patent');
                resetForm();
                if (oldTab !== 'patent') {
                  logTabSwitch(oldTab, 'patent');
                }
              }}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                activeTab === 'patent'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <div className="flex items-center">
                <FileText className="h-4 w-4 mr-2" />
                Patent Valuation
              </div>
            </button>
            <button
              onClick={() => {
                const oldTab = activeTab;
                setActiveTab('company');
                resetForm();
                if (oldTab !== 'company') {
                  logTabSwitch(oldTab, 'company');
                }
              }}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                activeTab === 'company'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <div className="flex items-center">
                <Building2 className="h-4 w-4 mr-2" />
                Company Valuation
              </div>
            </button>
          </div>
        </div>

        {/* Valuation Limit Indicator */}
        {isAdmin && valuationLimitInfo && (
          <div className="max-w-2xl mx-auto mb-6">
            <div className={`rounded-lg border p-4 ${
              valuationLimitInfo.remaining === 0 
                ? 'bg-red-50 border-red-200' 
                : valuationLimitInfo.remaining <= 1
                ? 'bg-yellow-50 border-yellow-200'
                : 'bg-blue-50 border-blue-200'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <BarChart2 className={`h-5 w-5 mr-2 ${
                    valuationLimitInfo.remaining === 0 
                      ? 'text-red-600' 
                      : valuationLimitInfo.remaining <= 1
                      ? 'text-yellow-600'
                      : 'text-blue-600'
                  }`} />
                  <span className={`text-sm font-medium ${
                    valuationLimitInfo.remaining === 0 
                      ? 'text-red-800' 
                      : valuationLimitInfo.remaining <= 1
                      ? 'text-yellow-800'
                      : 'text-blue-800'
                  }`}>
                    {valuationLimitInfo.remaining === 0 
                      ? 'No valuations remaining'
                      : `${valuationLimitInfo.remaining} of ${valuationLimitInfo.maxValuations} valuation${valuationLimitInfo.maxValuations !== 1 ? 's' : ''} remaining`
                    }
                  </span>
                </div>
                {valuationLimitInfo.remaining === 0 && (
                  <span className="text-xs text-red-600 font-medium">
                    Contact us for additional access
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Form */}
        <div className="max-w-2xl mx-auto">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="space-y-4">
                {activeTab === 'patent' ? (
                  <>
                    <div>
                      <label htmlFor="patentNumber" className="block text-sm font-medium text-gray-700">
                        Patent Number
                      </label>
                      <div className="mt-1 relative rounded-md shadow-sm">
                        <input
                          type="text"
                          name="patentNumber"
                          id="patentNumber"
                          value={patentNumber}
                          onChange={handlePatentNumberChange}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && isAdmin && patentNumber.trim()) {
                              handleSubmit(e);
                            }
                          }}
                          disabled={!isAdmin}
                          className={`block w-full pl-3 pr-12 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 ${
                            !isAdmin ? 'bg-gray-50 text-gray-400 cursor-not-allowed' :
                            error && activeTab === 'patent' && !patentNumber.trim()
                              ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                              : 'border-gray-300'
                          }`}
                          placeholder={!isAdmin ? "Contact us for access" : "Enter a valid, granted U.S. patent number (e.g., US11360440)"}
                        />
                        {isAdmin && (
                          <button
                            type="submit"
                            disabled={loading || !patentNumber.trim()}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-blue-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={(e) => {
                              e.preventDefault();
                              if (patentNumber.trim() && !loading) {
                                handleSubmit(e);
                              }
                            }}
                          >
                            <Search className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                      {!isAdmin && (
                        <p className="mt-1 text-sm text-gray-500">Demo access is restricted. Please contact us to request access.</p>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <label htmlFor="companyName" className="block text-sm font-medium text-gray-700">
                        Company Name
                      </label>
                      <div className="mt-1 relative rounded-md shadow-sm">
                        <input
                          type="text"
                          name="companyName"
                          id="companyName"
                          value={companyName}
                          onChange={handleCompanyNameChange}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && isAdmin && companyName.trim() && selectedCompany) {
                              handleSubmit(e);
                            }
                          }}
                          disabled={!isAdmin}
                          className={`block w-full pl-3 pr-12 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 ${
                            !isAdmin ? 'bg-gray-50 text-gray-400 cursor-not-allowed' :
                            error && activeTab === 'company' && !companyName.trim()
                              ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                              : 'border-gray-300'
                          }`}
                          placeholder={!isAdmin ? "Contact us for access" : "Enter a U.S. company name (e.g., Apple Inc.)"}
                        />
                        {isAdmin && (
                          <button
                            type="submit"
                            disabled={loading || !companyName.trim() || !selectedCompany}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-blue-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={(e) => {
                              e.preventDefault();
                              if (companyName.trim() && selectedCompany && !loading) {
                                handleSubmit(e);
                              }
                            }}
                          >
                            <Search className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                      {!isAdmin && (
                        <p className="mt-1 text-sm text-gray-500">Demo access is restricted. Please contact us to request access.</p>
                      )}
                    </div>
                    
                    {showCompanyList && (
                      <div className="mt-4 space-y-4">
                        <p className="text-sm text-gray-500">Select the correct company:</p>
                        {isSearching ? (
                          <div className="flex items-center justify-center py-4">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                            <span className="ml-3 text-sm text-gray-500">Searching companies...</span>
                          </div>
                        ) : filteredCompanies.length > 0 ? (
                        <div className="space-y-2">
                          {currentCompanies.map((company) => (
                            <button
                              key={company.id}
                              onClick={() => handleCompanySelect(company)}
                              className={`w-full text-left p-3 rounded-lg border transition-all duration-200 ${
                                selectedCompany?.id === company.id
                                  ? 'border-blue-500 bg-blue-50'
                                  : 'border-gray-200 hover:border-blue-300'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="font-medium text-gray-900">{company.name}</div>
                                <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  company.type === 'Listed'
                                    ? 'bg-purple-100 text-purple-800'
                                    : 'bg-indigo-100 text-indigo-800'
                                }`}>
                                  {company.type}
                                </div>
                              </div>
                              <div className="text-sm text-gray-500">
                                {company.industry} • {company.alivePatent?.toLocaleString() || 0} patents
                              </div>
                            </button>
                          ))}
                        </div>
                        ) : (
                          <div className="text-center py-4 text-sm text-gray-500">
                            No companies found
                          </div>
                        )}
                        
                        {/* Pagination Controls */}
                        {totalPages > 1 && (
                          <div className="flex items-center justify-between pt-4">
                            <div className="text-sm text-gray-500">
                              Showing {startIndex + 1}-{Math.min(endIndex, filteredCompanies.length)} of {filteredCompanies.length} results
                            </div>
                            <div className="flex space-x-2">
                              <button
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                disabled={currentPage === 1}
                                className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Previous
                              </button>
                              <button
                                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                disabled={currentPage === totalPages}
                                className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Next
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {selectedCompany && (
                      <div className="mt-4 p-3 rounded-lg border border-blue-500 bg-blue-50">
                        <div className="flex items-center justify-between">
                          <div className="font-medium text-gray-900">{selectedCompany.name}</div>
                          <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                            selectedCompany.type === 'Listed'
                              ? 'bg-purple-100 text-purple-800'
                              : 'bg-indigo-100 text-indigo-800'
                          }`}>
                            {selectedCompany.type}
                          </div>
                        </div>
                        <div className="text-sm text-gray-500">
                          {selectedCompany.industry} • {selectedCompany.alivePatent?.toLocaleString() || 0} patents
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {error && (
              <div className="rounded-md bg-red-50 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <X className="h-5 w-5 text-red-400" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">{error}</h3>
                  </div>
                </div>
              </div>
            )}

            {((activeTab === 'patent' && !result) || (activeTab === 'company' && !companyValuation)) && (
              <div className="flex justify-center">
                <button
                  type="submit"
                  disabled={!isAdmin || loading}
                  className={`inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg shadow-sm transition-all duration-300 ${
                    !isAdmin 
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                      : 'text-white bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 hover:from-blue-700 hover:via-indigo-700 hover:to-purple-700'
                  }`}
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Processing...
                    </>
                  ) : !isAdmin ? (
                    'Contact for Access'
                  ) : (
                    <>
                      <Search className="mr-2 h-5 w-5" />
                      Get Valuation
                    </>
                  )}
                </button>
              </div>
            )}
          </form>

          {/* Contact Prompt Modal with Access Code */}
          <ContactPromptModalWithAccess
            isOpen={showContactModal}
            onClose={handleCloseModal}
            onPreview={handlePreviewDemo}
          />

          {/* Results */}
          {result && activeTab === 'patent' && (
            <div className="mt-8 space-y-6 relative">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl shadow-sm overflow-hidden border border-blue-100">
                <div className="p-6">
                  <div className={`${isMobile ? 'space-y-3' : 'flex items-center justify-between'} md:flex md:items-center md:justify-between space-y-3 md:space-y-0`}>
                    {console.log('🔍 Mobile Debug:', { isMobile, windowWidth: window.innerWidth, activeTab })}
                    <div className={`${isMobile ? 'w-full' : ''} w-full md:w-auto`}>
                      <h2 className={`font-semibold text-gray-700 ${isMobile ? 'text-lg' : 'text-xl'} text-lg md:text-xl`}>Estimated Patent Value</h2>
                      <p className="text-sm text-gray-500 mt-1">Based on comprehensive analysis</p>
                    </div>
                    {result.insights?.medianValue && (
                      <div className={`${isMobile ? 'w-fit' : ''} w-fit px-3 py-1 rounded-full text-sm font-medium ${
                        parseFloat(result.value.replace(/[^0-9]/g, '')) > 
                        parseFloat(result.insights.medianValue.value.replace(/[^0-9.]/g, ''))
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {parseFloat(result.value.replace(/[^0-9]/g, '')) > 
                         parseFloat(result.insights.medianValue.value.replace(/[^0-9.]/g, ''))
                          ? 'Above Median'
                          : 'Below Median'
                        }
                      </div>
                    )}
                  </div>
                  <div className="mt-4">
                    <p className="text-6xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                      {formatPatentValue(result.value, isMobile)}
                    </p>
                    <p className="text-sm text-gray-500 mt-2">
                      Last updated: {result.timestamp}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100">
                  <h2 className="font-semibold text-gray-900">Featured Insights</h2>
                </div>
                <div className="p-6">
                  <div className="space-y-6">
                    {result.insights && Object.entries(result.insights).map(([key, insight]) => (
                      <div key={key} className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className={`w-10 h-10 rounded-full ${
                            key === 'patentGrade' ? 'bg-purple-50' :
                            key === 'medianValue' ? 'bg-indigo-50' :
                            key === 'patentCitations' ? 'bg-blue-50' :
                            'bg-green-50'
                          } flex items-center justify-center mr-4`}>
                            {key === 'patentGrade' ? <Star className="h-5 w-5 text-purple-600" /> :
                             key === 'medianValue' ? <BarChart2 className="h-5 w-5 text-indigo-600" /> :
                             key === 'patentCitations' ? <GitBranch className="h-5 w-5 text-blue-600" /> :
                             <Target className="h-5 w-5 text-green-600" />
                            }
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">{insight.label}</p>
                            <p className="text-sm text-gray-500">{insight.description}</p>
                          </div>
                        </div>
                        <div className={`text-sm font-medium ${
                          key === 'patentGrade' ? 
                            insight.value.startsWith('A') ? 'text-blue-600' :
                            insight.value.startsWith('B') ? 'text-green-600' :
                            'text-yellow-600' :
                          key === 'medianValue' ? 'text-gray-600' :
                          key === 'patentCitations' ? 'text-blue-600' :
                          key === 'marketGrowth' && insight.value.startsWith('-') ? 'text-red-600' :
                          'text-green-600'
                        }`}>
                          {insight.value}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Get Full Report Button */}
              <div className="mt-8">
                <button 
                  onClick={() => {
                    setShowEmailModal(true);
                    setEmailSubmitted(false);
                    setEmail('');
                    setError(null);
                  }}
                  className="w-full inline-flex justify-center items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg shadow-sm text-white bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 hover:from-blue-700 hover:via-indigo-700 hover:to-purple-700 transition-all duration-300"
                >
                  Get Full Report
                  <ArrowRight className="ml-2 h-5 w-5" />
                </button>
              </div>

              {/* Email Collection Modal */}
              {showEmailModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div className="bg-white rounded-xl p-8 max-w-md w-full mx-4">
                    <div className="flex justify-between items-center mb-6">
                      <h3 className="text-xl font-semibold text-gray-900">Get Your Full Report</h3>
                      <button 
                        onClick={() => {
                          setShowEmailModal(false);
                          setEmailSubmitted(false);
                          setEmail('');
                          setError(null);
                        }}
                        className="text-gray-400 hover:text-gray-500"
                      >
                        <XCircle className="h-6 w-6" />
                      </button>
                    </div>
                    
                    {!emailSubmitted ? (
                      <>
                        <p className="text-gray-600 mb-4">
                          Enter your email to receive the complete valuation report with detailed analysis and insights.
                        </p>
                        {error && (
                          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                            <p className="text-sm text-red-600">{error}</p>
                          </div>
                        )}
                        <form onSubmit={async (e) => {
                          e.preventDefault();
                          
                          // Validate email format
                          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                          if (!emailRegex.test(email)) {
                            setError('Please enter a valid email address');
                            return;
                          }
                          
                          setEmailLoading(true);
                          setError(null);
                          
                          try {
                            // Track email collection analytics
                            const source = activeTab === 'patent' ? 'patent_report' : 'company_report';
                            const identifier = activeTab === 'patent' ? patentNumber : (selectedCompany ? selectedCompany.name : companyName);
                            logEmailCollection(email, source, identifier);
                            
                            // Prepare email data
                            const reportType = activeTab === 'patent' ? 'Patent' : 'Company';
                            const reportSubject = `Full ${reportType} Valuation Report Request`;
                            const reportMessage = `Request for full ${reportType.toLowerCase()} valuation report:\n\n${reportType}: ${identifier}\n\nEmail: ${email}\n\nPlease send the complete valuation report to the above email address.`;
                            
                            const emailData = {
                              name: email.split('@')[0], // Use email username as name
                              email: email,
                              subject: reportSubject,
                              message: reportMessage
                            };
                            
                            // Send email
                            await apiService.sendEmail(emailData);
                            
                            setEmailSubmitted(true);
                          } catch (err) {
                            setError(err.message || 'Failed to send email. Please try again.');
                          } finally {
                            setEmailLoading(false);
                          }
                        }}>
                          <div className="mb-4">
                            <label htmlFor="identifier" className="block text-sm font-medium text-gray-700 mb-1">
                              {activeTab === 'patent' ? 'Patent Number' : 'Company Name'}
                            </label>
                            <input
                              type="text"
                              id="identifier"
                              value={activeTab === 'patent' ? patentNumber : (selectedCompany ? selectedCompany.name : companyName)}
                              readOnly
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                            />
                          </div>
                          <div className="mb-4">
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                              Email Address
                            </label>
                            <input
                              type="email"
                              id="email"
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="your@email.com"
                              required
                              disabled={emailLoading}
                            />
                          </div>
                          <button
                            type="submit"
                            disabled={emailLoading}
                            className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-base font-medium rounded-lg shadow-sm text-white bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 hover:from-blue-700 hover:via-indigo-700 hover:to-purple-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {emailLoading ? 'Sending...' : 'Send Report'}
                            {!emailLoading && <ArrowRight className="ml-2 h-5 w-5" />}
                          </button>
                        </form>
                      </>
                    ) : (
                      <div className="text-center">
                        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                          <Check className="h-6 w-6 text-green-600" />
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">Thank You!</h3>
                        <p className="text-gray-600 mb-4">
                          Your report will be sent to {email} by one of our representatives.
                        </p>
                        <p className="text-sm text-gray-500">
                          If you do not receive a response in a few days, please contact our team at{' '}
                          <a href="mailto:pricepatent@wisdomain.com" className="text-blue-600 hover:text-blue-800 underline">
                            pricepatent@wisdomain.com
                          </a>
                          .
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {companyValuation && activeTab === 'company' && (
            <div className="mt-8 space-y-6">
              {isListedCompany ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Valuation Status Card */}
                  <div className={`rounded-xl shadow-sm overflow-hidden border h-full ${
                    result.valuation?.toLowerCase().includes('over') ? 'bg-gradient-to-br from-red-50 to-red-100 border-red-200' : 
                    result.valuation?.toLowerCase().includes('under') ? 'bg-gradient-to-br from-green-50 to-green-100 border-green-200' :
                    'bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200'
                  }`}>
                    <div className="p-6 h-full flex flex-col justify-between">
                      <div>
                        <div>
                          <h2 className="text-xl font-semibold text-gray-700">Stock Valuation Status</h2>
                          <p className="text-sm text-gray-500 mt-1">Based on market analysis</p>
                        </div>
                        <div className="mt-4">
                          <div className={
                            result.valuation?.toLowerCase().includes('over') ? 'text-red-600' : 
                            result.valuation?.toLowerCase().includes('under') ? 'text-green-600' :
                            'text-yellow-600'
                          }>
                            <p className="text-5xl font-extrabold whitespace-nowrap">
                              {result.valuation?.toLowerCase().includes('under') ? 'Undervalued' :
                               result.valuation?.toLowerCase().includes('over') ? 'Overvalued' :
                               'Fair Value'}
                            </p>
                          </div>
                        </div>
                      </div>
                      <p className="text-sm text-gray-500">
                        Last updated: {result.timestamp}
                      </p>
                    </div>
                  </div>

                  {/* Portfolio Value Card */}
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl shadow-sm overflow-hidden border border-blue-100 h-full">
                    <div className="p-6 h-full flex flex-col justify-between">
                      <div>
                        <div>
                          <h2 className="text-xl font-semibold text-gray-700">Estimated Portfolio Value</h2>
                          <p className="text-sm text-gray-500 mt-1">Using advanced patent analysis</p>
                        </div>
                        <div className="mt-4">
                          <p className="text-6xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                            {formatValue(result.estimatedPortfolioValue) || '$0'}
                          </p>
                        </div>
                      </div>
                      <p className="text-sm text-gray-500">
                        Last updated: {result.timestamp}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl shadow-sm overflow-hidden border border-emerald-100">
                    <div className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <h2 className="text-xl font-semibold text-gray-700">Estimated Company Value</h2>
                          <p className="text-sm text-gray-500 mt-1">Powered by advanced AI analysis</p>
                        </div>
                      </div>
                      <div className="mt-4">
                        <p className="text-6xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-600">
                          {formatValue(result.estimatedValue) || '$0'}
                        </p>
                        <p className="text-sm text-gray-500 mt-2">
                          Last updated: {result.timestamp}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl shadow-sm overflow-hidden border border-blue-100">
                    <div className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <h2 className="text-xl font-semibold text-gray-700">Estimated Portfolio Value</h2>
                          <p className="text-sm text-gray-500 mt-1">Using advanced patent analysis</p>
                        </div>
                      </div>
                      <div className="mt-4">
                        <p className="text-6xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                          {formatValue(result.estimatedPortfolioValue) || '$0'}
                        </p>
                        <p className="text-sm text-gray-500 mt-2">
                          Last updated: {result.timestamp}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100">
                  <h2 className="font-semibold text-gray-900">Featured Insights</h2>
                </div>
                <div className="p-6">
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center mr-4">
                          <Award className="h-5 w-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">Technology Strength</p>
                          <p className="text-sm text-gray-500">Overall grade</p>
                        </div>
                      </div>
                      <div className={`text-sm font-medium ${
                        result.grade?.startsWith('A') ? 'text-blue-600' :
                        result.grade?.startsWith('B') ? 'text-green-600' :
                        'text-yellow-600'
                      }`}>
                        {result.grade || 'N/A'}
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center mr-4">
                          <FileText className="h-5 w-5 text-green-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">Patent Portfolio</p>
                          <p className="text-sm text-gray-500">Active patents</p>
                        </div>
                      </div>
                      <div className="text-sm font-medium text-gray-600">
                        {result.patents?.toLocaleString() || '0'}
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-full bg-pink-50 flex items-center justify-center mr-4">
                          <Users className="h-5 w-5 text-pink-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">Competitor Comparison</p>
                          <p className="text-sm text-gray-500">Market position</p>
                        </div>
                      </div>
                      <div className={`text-sm font-medium ${
                        result.marketPosition === 'Leader' ? 'text-blue-600' :
                        result.marketPosition === 'Diverse' ? 'text-green-600' :
                        result.marketPosition === 'Specialized' ? 'text-purple-600' :
                        result.marketPosition === 'Weak' ? 'text-yellow-600' :
                        'text-gray-600'
                      }`}>
                        {result.marketPosition || 'N/A'}
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-full bg-indigo-50 flex items-center justify-center mr-4">
                          <Globe className="h-5 w-5 text-indigo-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">Global Coverage</p>
                          <p className="text-sm text-gray-500">Other countries with patents</p>
                        </div>
                      </div>
                      <div className="text-sm font-medium text-indigo-600">
                        {result.countries?.toLocaleString() || '0'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Get Full Report Button */}
              <div className="mt-8">
                <button 
                  onClick={() => {
                    setShowEmailModal(true);
                    setEmailSubmitted(false);
                    setEmail('');
                    setError(null);
                  }}
                  className="w-full inline-flex justify-center items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg shadow-sm text-white bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 hover:from-blue-700 hover:via-indigo-700 hover:to-purple-700 transition-all duration-300"
                >
                  Get Full Report
                  <ArrowRight className="ml-2 h-5 w-5" />
                </button>
              </div>

              {/* Email Collection Modal */}
              {showEmailModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div className="bg-white rounded-xl p-8 max-w-md w-full mx-4">
                    <div className="flex justify-between items-center mb-6">
                      <h3 className="text-xl font-semibold text-gray-900">Get Your Full Report</h3>
                      <button 
                        onClick={() => {
                          setShowEmailModal(false);
                          setEmailSubmitted(false);
                          setEmail('');
                          setError(null);
                        }}
                        className="text-gray-400 hover:text-gray-500"
                      >
                        <XCircle className="h-6 w-6" />
                      </button>
                    </div>
                    
                    {!emailSubmitted ? (
                      <>
                        <p className="text-gray-600 mb-4">
                          Enter your email to receive the complete valuation report with detailed analysis and insights.
                        </p>
                        {error && (
                          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                            <p className="text-sm text-red-600">{error}</p>
                          </div>
                        )}
                        <form onSubmit={async (e) => {
                          e.preventDefault();
                          
                          // Validate email format
                          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                          if (!emailRegex.test(email)) {
                            setError('Please enter a valid email address');
                            return;
                          }
                          
                          setEmailLoading(true);
                          setError(null);
                          
                          try {
                            // Track email collection analytics
                            const source = activeTab === 'patent' ? 'patent_report' : 'company_report';
                            const identifier = activeTab === 'patent' ? patentNumber : (selectedCompany ? selectedCompany.name : companyName);
                            logEmailCollection(email, source, identifier);
                            
                            // Prepare email data
                            const reportType = activeTab === 'patent' ? 'Patent' : 'Company';
                            const reportSubject = `Full ${reportType} Valuation Report Request`;
                            const reportMessage = `Request for full ${reportType.toLowerCase()} valuation report:\n\n${reportType}: ${identifier}\n\nEmail: ${email}\n\nPlease send the complete valuation report to the above email address.`;
                            
                            const emailData = {
                              name: email.split('@')[0], // Use email username as name
                              email: email,
                              subject: reportSubject,
                              message: reportMessage
                            };
                            
                            // Send email
                            await apiService.sendEmail(emailData);
                            
                            setEmailSubmitted(true);
                          } catch (err) {
                            setError(err.message || 'Failed to send email. Please try again.');
                          } finally {
                            setEmailLoading(false);
                          }
                        }}>
                          <div className="mb-4">
                            <label htmlFor="identifier" className="block text-sm font-medium text-gray-700 mb-1">
                              {activeTab === 'patent' ? 'Patent Number' : 'Company Name'}
                            </label>
                            <input
                              type="text"
                              id="identifier"
                              value={activeTab === 'patent' ? patentNumber : (selectedCompany ? selectedCompany.name : companyName)}
                              readOnly
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                            />
                          </div>
                          <div className="mb-4">
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                              Email Address
                            </label>
                            <input
                              type="email"
                              id="email"
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="your@email.com"
                              required
                              disabled={emailLoading}
                            />
                          </div>
                          <button
                            type="submit"
                            disabled={emailLoading}
                            className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-base font-medium rounded-lg shadow-sm text-white bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 hover:from-blue-700 hover:via-indigo-700 hover:to-purple-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {emailLoading ? 'Sending...' : 'Send Report'}
                            {!emailLoading && <ArrowRight className="ml-2 h-5 w-5" />}
                          </button>
                        </form>
                      </>
                    ) : (
                      <div className="text-center">
                        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                          <Check className="h-6 w-6 text-green-600" />
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">Thank You!</h3>
                        <p className="text-gray-600 mb-4">
                          Your report will be sent to {email} by one of our representatives.
                        </p>
                        <p className="text-sm text-gray-500">
                          If you do not receive a response in a few days, please contact our team at{' '}
                          <a href="mailto:pricepatent@wisdomain.com" className="text-blue-600 hover:text-blue-800 underline">
                            pricepatent@wisdomain.com
                          </a>
                          .
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DemoPage; 